import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAtom, useAtomValue, useSetAtom } from 'jotai/react'

import useSolana from '@/lib/solana/hooks/useSolana'
import { services } from '@/stores'

import useFetch, { Method } from './useFetch'
import { UserInfo } from './useUserInfo'

export type LoginWallet = {
  token: string
  userInfo: UserInfo
}

export default function useLoginWallet(
  signature: string,
  message: string,
  onSuccess?: () => void
) {
  const { t } = useTranslation()
  const { token, address } = useToken()
  const [, setTokenMap] = useAtom(services.tokenMapAtom)
  const setUserInfo = useSetAtom(services.userInfoAtom)

  return useFetch<LoginWallet>(
    address && !token && signature && message ? '/user/login' : null,
    Method.POST,
    { signature, message, wallet: address, regType: 1 },
    {
      onSuccess: (data) => {
        setTokenMap((prev) => ({ ...prev, [address!]: data.token }))
        setUserInfo(data.userInfo)
        onSuccess?.()
      },
      onError: () => {
        toast.error(t('wallet.loginError'))
      }
    }
  )
}

export const useToken = () => {
  const { address } = useSolana()
  const tokenMap = useAtomValue(services.tokenMapAtom)

  const token = useMemo(
    () => (address && tokenMap[address] ? tokenMap[address] : undefined),
    [address, tokenMap]
  )

  return {
    token,
    address
  }
}
